import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupPrintTicketButtons } from 'features/common/components/Punchup/composites/tickets/PunchupPrintTicketButtons';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PrintFormat, usePrintTickets } from 'features/common/hooks/usePrintTickets';
import { postToast } from 'features/common/utils/postFlash';

import { RecentComp } from '../../types';

import styles from './SendTicketsRecentComps.module.css';

export interface SendTicketsRecentCompsProps {
	comps: RecentComp[];
	isLoading?: boolean;
	/** True when the read failed. An error must not render as "no comps sent" — that tells the
	 *  operator the show has none when the truth is that nobody asked successfully. */
	isError?: boolean;
	onRetry?: () => void;
}

const printKeyFor = (comp: RecentComp, format: PrintFormat) => `${comp.transactionId}:${format}`;

export const SendTicketsRecentComps: FC<SendTicketsRecentCompsProps> = ({
	comps,
	isLoading = false,
	isError = false,
	onRetry,
}) => {
	const { t } = useTranslation();
	const { printingKey, isPrinting, print } = usePrintTickets({
		onError: (reason) =>
			postToast(
				'error',
				reason === 'fetch'
					? t('send-tickets-recent-print-load-error', 'Could not load the ticket PDF. Please try again.')
					: t('send-tickets-recent-print-error', 'Could not open the print dialog. Please try again.')
			),
	});

	const printingFormatFor = (comp: RecentComp): PrintFormat | null => {
		if (printingKey === printKeyFor(comp, 'thermal')) return 'thermal';
		if (printingKey === printKeyFor(comp, 'standard')) return 'standard';
		return null;
	};

	return (
		<section className={styles.root} aria-labelledby="send-tickets-recent-comps-title">
			<div>
				<h3 id="send-tickets-recent-comps-title" className={styles.heading}>
					{t('send-tickets-recent-title', 'Recent comps')}
				</h3>
				<p className={styles.description}>
					{t('send-tickets-recent-desc', 'The latest complimentary tickets for this show. Print any of them.')}
				</p>
			</div>

			{isError && comps.length === 0 ? (
				<PunchupErrorState
					title={t('send-tickets-recent-error-title', "We couldn't load recent comps")}
					description={t('send-tickets-recent-error-desc', 'The list could not be fetched. Sending still works.')}
					onRetry={onRetry}
				/>
			) : isLoading && comps.length === 0 ? (
				<PunchupSpinner size={20} />
			) : comps.length === 0 ? (
				<p className={styles.empty}>
					{t('send-tickets-recent-empty', 'No comps sent for this show yet. The ones you send will appear here.')}
				</p>
			) : (
				<div className={styles.list}>
					{comps.map((comp) => (
						<div key={comp.transactionId} className={styles.row}>
							{comp.justSent && (
								<span className={styles.justSent}>{t('send-tickets-recent-just-sent', 'Just sent')}</span>
							)}
							<div className={styles.identity}>
								<span className={styles.name}>
									{comp.name || comp.email || t('send-tickets-recent-no-name', 'No name')}
								</span>
								<span className={styles.meta}>
									{[
										comp.name && comp.email ? comp.email : '',
										comp.quantity === 1
											? t('send-tickets-recent-quantity', '{{count}} ticket', { count: comp.quantity })
											: t('send-tickets-recent-quantity_plural', '{{count}} tickets', {
													count: comp.quantity,
											  }),
										comp.ticketTypeNames.join(', '),
									]
										.filter(Boolean)
										.join(' · ')}
								</span>
							</div>
							<PunchupPrintTicketButtons
								size="small"
								tickets={{ transactionId: comp.transactionId }}
								printing={printingFormatFor(comp)}
								busy={isPrinting}
								onPrint={(format) => print(printKeyFor(comp, format), { transactionId: comp.transactionId }, format)}
								labels={{
									thermal: t('send-tickets-recent-print-thermal', 'Print thermal'),
									standard: t('send-tickets-recent-print-standard', 'Print standard'),
									thermalHint: t('send-tickets-recent-print-thermal-hint', '80mm receipt-printer format.'),
									standardHint: t('send-tickets-recent-print-standard-hint', 'Letter-size ticket format.'),
									thermalUnavailable: t('send-tickets-recent-print-unavailable', 'This comp has no printable tickets.'),
									standardUnavailable: t(
										'send-tickets-recent-print-standard-unavailable',
										'Letter-size printing is not available for this comp.'
									),
								}}
							/>
						</div>
					))}
				</div>
			)}
		</section>
	);
};
